<template>
  <BaseInnerLoading v-if="isRetrievingService" />
  <template v-else-if="service">
    <form class="space-y-6" @submit="onSubmit">
      <div class="text-center">
        <p class="text-2xl font-bold leading-tight">
          {{ service.name }}
        </p>

        <p class="text-lg text-background-fg/70">
          {{ service.description }}
        </p>
      </div>

      <FormInputText
        name="customer.name"
        label="Customer Name"
      />

      <FormInputText
        name="customer.email"
        label="Customer Email"
      />

      <FormInputText
        name="customer.phone"
        label="Customer Phone"
      />

      <FormInputText
        name="description"
        label="Description"
      />

      <FormInputPrice
        name="amountNet"
        label="Price"
        :disabled="disablePriceInput"
      />

      <div v-if="currentTenant?.includeVat">
        <div class="flex items-center justify-between">
          <FormFieldLabel> VAT (20%) </FormFieldLabel>

          <p>
            {{
              formatToCurrencyString(
                (values.amountNet ?? 0) * 0.2
              )
            }}
          </p>
        </div>

        <div class="flex items-center justify-between">
          <FormFieldLabel> Total </FormFieldLabel>

          <p>
            {{
              formatToCurrencyString(
                (values.amountNet ?? 0) * 1.2
              )
            }}
          </p>
        </div>
      </div>

      <FormSubmitButton
        class="w-full"
        :is-loading="isSubmitting"
      >
        Pay
        {{
          formatToCurrencyString(
            (values.amountNet ?? 0) *
              (currentTenant?.includeVat ? 1.2 : 1)
          )
        }}
      </FormSubmitButton>
    </form>
  </template>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type { RetrieveServiceResponse } from "@/services/services/retrieveService"
import router from "@/router"

const props = defineProps<{
  serviceId?: string
}>()

const { currentTenant } = useCurrentTenant()
const createPaymentRequestFormSchema = z
  .object({
    customer: z
      .object({
        name: z.string().min(1, "Name must not be empty"),
        email: z.union([
          z.literal(""),
          z
            .string()
            .email("Please enter a valid email address")
            .nullable(),
        ]),
        phone: z.string().nullable(),
      })
      .superRefine((params, ctx) => {
        if (!params.email && !params.phone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Either email or phone is required",
            path: ["email"],
          })

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Either email or phone is required",
            path: ["phone"],
          })
        }
      }),
    description: z.string(),
    amountNet: z.number().positive(),
  })
  .superRefine((params, ctx) => {
    if (params.amountNet <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price must be greater than 0",
        path: ["amountNet"],
      })
    }
  })

const {
  handleSubmit,
  values,
  isSubmitting,
  setValues,
  setErrors,
} = useForm({
  validationSchema: toTypedSchema(
    createPaymentRequestFormSchema
  ),
  initialValues: {
    customer: {
      name: "",
      email: "",
      phone: "",
    },
    description: "",
    amountNet: 0,
  },
})

const onSubmit = handleSubmit(async (params) => {
  // if (isSubmitting.value) return
  if (!service.value) return

  try {
    const { ident } = await createPaymentRequest({
      ...params,
      description: `${service.value.name} - ${params.description}`,
      vatCode: currentTenant.value?.includeVat
        ? "T1"
        : "T0",
      customerCreated: true,
    })
    gotoPaymentCheckout(ident)
  } catch (error) {
    if (error instanceof ApiError) {
      setErrors(error.requestErrors?.body ?? {})
    }
    toast.error("Failed to submit payment")
  }
})

const service = ref<RetrieveServiceResponse>()
const isRetrievingService = ref(false)
onMounted(async () => {
  if (props.serviceId) {
    isRetrievingService.value = true

    try {
      isRetrievingService.value = true
      service.value = await retrieveService(props.serviceId)
      setValues({
        amountNet: service.value.cost ?? 0,
      })
    } catch {
      toast.error("Failed to retrieve service")
      router.push({
        name: "servicesIndex",
      })
    } finally {
      isRetrievingService.value = false
    }
  }
})

const disablePriceInput = computed(() =>
  Boolean(service.value?.cost)
)
</script>
